import { UseEducationProviderQueryOptions } from '@/modules/common/hooks/useEducationProviderOptions';
import { getUseCurrentUserOptions } from '@/modules/common/hooks/useUser';
import { AdminLayout } from '@/modules/common/layouts/AdminLayout';
import { CompanySettingsPage } from '@/modules/settings/pages/CompanySettingsPage';
import { EducationProviderSettingsPage } from '@/modules/settings/pages/EducationProviderSettingsPage';
import { UserInclude, UserRole } from '@/types/user';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import * as yup from 'yup';

export type CompanySettingsSearchParams = {
  tab?: 'organization' | 'learning-budget';
  limit?: number;
  page?: number;
};

export const Route = createFileRoute('/_admin/settings/')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const { data: user } = await queryClient.ensureQueryData(
      getUseCurrentUserOptions([
        UserInclude.Company,
        UserInclude.TaxBenefitProviders,
      ]),
    );

    if (user.educationProvider) {
      await queryClient.ensureQueryData(
        UseEducationProviderQueryOptions(user.educationProvider),
      );
    }

    return {
      user,
    };
  },
  validateSearch: (
    search: Record<string, unknown>,
  ): CompanySettingsSearchParams => {
    return {
      tab: yup
        .string()
        .oneOf(['organization', 'learning-budget'])
        .optional()
        .validateSync(search.tab),
      limit: yup.number().optional().validateSync(search.limit),
      page: yup.number().optional().validateSync(search.page),
    };
  },
  component: function SettingsPageComponentSwitcher() {
    const { user } = useRouteContext({ from: Route.id });

    if (!user) {
      return null;
    }

    const isEducationProviderUser = user?.roles.includes(
      UserRole.EducationProvider,
    );

    return (
      <AdminLayout hideBanners>
        {isEducationProviderUser ? (
          <EducationProviderSettingsPage />
        ) : (
          <CompanySettingsPage />
        )}
      </AdminLayout>
    );
  },
});
